/**
 * Services API pour le traitement de paie
 * Communication avec le backend pour toutes les opérations de paie
 */

import {
  PayrollPeriod,
  PayrollBatch,
  PayrollVariable,
  PayrollCalculation,
  PayrollEmployee,
  PayrollPeriodFormData,
  PayrollVariableFormData,
  PayrollBatchFormData,
  PayrollStatistics,
  PayrollPeriodFilters,
  APIResponse,
  PaginatedResponse,
} from "../../../types/hr";

const API_BASE = "/api/hr/payroll-processing";

const getSelectedCompanyId = (): number | null => {
  try {
    const storedCompany = localStorage.getItem("selectedCompany");
    if (
      storedCompany === null ||
      storedCompany === "null" ||
      storedCompany === "undefined"
    ) {
      return null;
    }

    const parsedCompany = Number(JSON.parse(storedCompany));
    return Number.isFinite(parsedCompany) ? parsedCompany : null;
  } catch {
    return null;
  }
};

class PayrollAPI {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = localStorage.getItem("authToken");
    const companyId = getSelectedCompanyId();
    const endpointWithCompany =
      companyId !== null && !/[?&]company_id=/.test(endpoint)
        ? `${endpoint}${endpoint.includes("?") ? "&" : "?"}company_id=${encodeURIComponent(
            String(companyId)
          )}`
        : endpoint;

    const headers = new Headers(options.headers);
    headers.set('Authorization', `Bearer ${token}`);
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    if (companyId !== null) headers.set('X-Company-Id', String(companyId));
    const response = await fetch(`${API_BASE}${endpointWithCompany}`, { ...options, headers });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      const errorMessage =
        error?.message ||
        error?.error ||
        (Array.isArray(error?.errors) ? error.errors.join(", ") : null) ||
        "Erreur reseau";
      const apiError = new Error(
        errorMessage || `Erreur ${response.status}`
      ) as Error & { details?: any };
      apiError.details = error;
      throw apiError;
    }

    return response.json();
  }

  // ===== GESTION DES PÉRIODES =====

  async getPeriods(filters?: PayrollPeriodFilters): Promise<PayrollPeriod[]> {
    const queryParams = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const response = await this.request<APIResponse<PayrollPeriod[]>>(
      `/periods?${queryParams.toString()}`
    );
    return response.data || [];
  }

  async createPeriod(data: PayrollPeriodFormData): Promise<PayrollPeriod> {
    const response = await this.request<APIResponse<PayrollPeriod>>(
      "/periods",
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
    return response.data!;
  }

  async updatePeriod(
    id: number,
    data: Partial<PayrollPeriodFormData>
  ): Promise<PayrollPeriod> {
    const response = await this.request<APIResponse<PayrollPeriod>>(
      `/periods/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      }
    );
    return response.data!;
  }

  async deletePeriod(id: number): Promise<void> {
    await this.request(`/periods/${id}`, { method: "DELETE" });
  }

  // ===== GESTION DES EMPLOYÉS DE PAIE =====

  async getPayrollEmployees(periodId: number): Promise<PayrollEmployee[]> {
    const response = await this.request<APIResponse<PayrollEmployee[]>>(
      `/periods/${periodId}/employees`
    );
    return response.data || [];
  }

  async addEmployeesToPeriod(
    periodId: number,
    employeeIds: number[]
  ): Promise<void> {
    await this.request(`/periods/${periodId}/employees`, {
      method: "POST",
      body: JSON.stringify({ employee_ids: employeeIds }),
    });
  }

  async removeEmployeeFromPeriod(
    periodId: number,
    employeeId: number
  ): Promise<void> {
    await this.request(`/periods/${periodId}/employees/${employeeId}`, {
      method: "DELETE",
    });
  }

  // ===== GESTION DES VARIABLES =====

  async getVariables(periodId: number): Promise<PayrollVariable[]> {
    const response = await this.request<APIResponse<PayrollVariable[]>>(
      `/periods/${periodId}/variables`
    );
    return response.data || [];
  }

  async getEmployeeVariables(
    periodId: number,
    employeeId: number
  ): Promise<PayrollVariable | null> {
    try {
      const response = await this.request<APIResponse<PayrollVariable>>(
        `/periods/${periodId}/employees/${employeeId}/variables`
      );
      return response.data || null;
    } catch {
      return null;
    }
  }

  async setEmployeeVariables(
    periodId: number,
    employeeId: number,
    data: PayrollVariableFormData
  ): Promise<PayrollVariable> {
    const response = await this.request<APIResponse<PayrollVariable>>(
      `/periods/${periodId}/employees/${employeeId}/variables`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
    return response.data!;
  }

  async updateEmployeeVariables(
    periodId: number,
    employeeId: number,
    data: Partial<PayrollVariableFormData>
  ): Promise<PayrollVariable> {
    const response = await this.request<APIResponse<PayrollVariable>>(
      `/periods/${periodId}/employees/${employeeId}/variables`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      }
    );
    return response.data!;
  }

  async bulkSetVariables(
    periodId: number,
    variables: PayrollVariableFormData[]
  ): Promise<void> {
    await this.request(`/periods/${periodId}/variables/bulk`, {
      method: "POST",
      body: JSON.stringify({ variables }),
    });
  }

  // ===== CALCUL DE PAIE =====

  async calculatePayroll(
    periodId: number,
    employeeIds?: number[]
  ): Promise<PayrollBatch> {
    const response = await this.request<APIResponse<PayrollBatch>>(
      `/periods/${periodId}/calculate`,
      {
        method: "POST",
        body: JSON.stringify({ employee_ids: employeeIds }),
      }
    );
    return response.data!;
  }

  async getCalculations(periodId: number): Promise<PayrollCalculation[]> {
    const response = await this.request<APIResponse<PayrollCalculation[]>>(
      `/periods/${periodId}/calculations`
    );
    return response.data || [];
  }

  async getEmployeeCalculation(
    periodId: number,
    employeeId: number
  ): Promise<PayrollCalculation | null> {
    try {
      const response = await this.request<APIResponse<PayrollCalculation>>(
        `/periods/${periodId}/employees/${employeeId}/calculation`
      );
      return response.data || null;
    } catch {
      return null;
    }
  }

  async validateCalculations(
    periodId: number,
    employeeIds?: number[]
  ): Promise<void> {
    await this.request(`/periods/${periodId}/validate`, {
      method: "POST",
      body: JSON.stringify({ employee_ids: employeeIds }),
    });
  }

  async reopenCalculations(periodId: number): Promise<void> {
    await this.request(`/periods/${periodId}/calculations/reopen`, {
      method: "POST",
    });
  }

  // ===== LOTS DE TRAITEMENT =====

  async getBatch(batchId: number): Promise<PayrollBatch> {
    const response = await this.request<APIResponse<PayrollBatch>>(
      `/batches/${batchId}`
    );
    return response.data!;
  }

  async getBatches(filters?: {
    period_id?: number;
    status?: string;
  }): Promise<PayrollBatch[]> {
    const queryParams = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const response = await this.request<APIResponse<PayrollBatch[]>>(
      `/batches?${queryParams.toString()}`
    );
    return response.data || [];
  }

  // ===== STATISTIQUES =====

  async getStatistics(periodId: number): Promise<PayrollStatistics> {
    const response = await this.request<APIResponse<PayrollStatistics & { overall?: PayrollStatistics; period?: { id: number } }>>(
      `/periods/${periodId}/statistics`
    );
    if (!response.data) throw new Error("Statistiques de période indisponibles");
    return { ...(response.data.overall || response.data), period_id: response.data.period?.id || periodId };
  }

  // ===== EXPORT ET GÉNÉRATION =====

  async generatePayslips(
    periodId: number,
    employeeIds?: number[]
  ): Promise<string> {
    const response = await this.request<{ download_url: string }>(
      `/periods/${periodId}/payslips`,
      {
        method: "POST",
        body: JSON.stringify({ employee_ids: employeeIds }),
      }
    );
    return response.download_url;
  }

  async exportPayrollData(
    periodId: number,
    format: "excel" | "csv" | "pdf"
  ): Promise<string> {
    const response = await this.request<{ download_url: string }>(
      `/periods/${periodId}/export`,
      {
        method: "POST",
        body: JSON.stringify({ format }),
      }
    );
    return response.download_url;
  }
}

export const payrollAPI = new PayrollAPI();

// Exports individuels pour faciliter l'utilisation
export const {
  getPeriods,
  createPeriod,
  updatePeriod,
  deletePeriod,
  getPayrollEmployees,
  addEmployeesToPeriod,
  removeEmployeeFromPeriod,
  getVariables,
  getEmployeeVariables,
  setEmployeeVariables,
  updateEmployeeVariables,
  bulkSetVariables,
  calculatePayroll,
  getCalculations,
  getEmployeeCalculation,
  validateCalculations,
  reopenCalculations,
  getBatch,
  getBatches,
  getStatistics,
  generatePayslips,
  exportPayrollData,
} = payrollAPI;
